import { fetchAllPvSize } from './allPvSizeService';
import { supabaseClient } from './supabase';
import { parseSourceNumber } from '../utils/sourceNumeric';
import { isSolarApprovalCohortYear, SOLAR_ANALYSIS_CUTOFF_YEAR } from './solarAnalysisCutoff';

const POST_2015_COHORT_BASELINE_YEAR = 2015;

export type SolarPerCapitaSnapshot = {
  /** Population denominator year */
  latestYear: number | null;
  localSolarKwPerResident: number | null;
  latestCapacityKw: number | null;
  sameYearPopulation: number | null;
};

export type SolarCumulativeSnapshot = {
  latestYear: number | null;
  cumulativeSolarMw: number | null;
  latestAnnualAdditionMw: number | null;
  baselineYear: number | null;
  baselineCumulativeMw: number | null;
  cumulativeGrowthPct: number | null;
  post2015CohortSharePct: number | null;
};

export type SolarCumulativeSeriesPoint = {
  year: number;
  cumulativeKw: number;
  annualKw: number;
};

export function findSolarGrowthBaseline(
  series: SolarCumulativeSeriesPoint[],
  preferredYear = 2015
): SolarCumulativeSeriesPoint | null {
  if (!series.length) return null;
  const byYear = new Map(series.map((s) => [s.year, s]));
  const preferred = byYear.get(preferredYear);
  if (preferred && preferred.cumulativeKw > 0) return preferred;

  const afterPreferred = series
    .filter((s) => s.year > preferredYear && s.cumulativeKw > 0)
    .sort((a, b) => a.year - b.year);
  return afterPreferred[0] ?? null;
}

export function computeCumulativeGrowthPct(
  latest: SolarCumulativeSeriesPoint,
  baseline: SolarCumulativeSeriesPoint | null
): number | null {
  if (!baseline || baseline.cumulativeKw <= 0) return null;
  if (latest.year < baseline.year) return null;
  return ((latest.cumulativeKw - baseline.cumulativeKw) / baseline.cumulativeKw) * 100;
}

export function computePost2015CohortSharePct(series: SolarCumulativeSeriesPoint[]): number | null {
  let totalKw = 0;
  let postKw = 0;
  for (const point of series) {
    if (point.annualKw <= 0) continue;
    totalKw += point.annualKw;
    if (point.year > POST_2015_COHORT_BASELINE_YEAR) {
      postKw += point.annualKw;
    }
  }
  if (totalKw <= 0) return null;
  return (postKw / totalKw) * 100;
}

function annualKwByYear(
  records: { year: number | string; sector?: string | null; total_watts?: number | string | null }[]
): Map<number, number> {
  const yearMap = new Map<number, number>();
  for (const record of records) {
    const year = parseInt(String(record.year), 10);
    if (!isSolarApprovalCohortYear(year)) continue;
    const watts = parseSourceNumber(record.total_watts);
    if (watts == null) continue;
    yearMap.set(year, (yearMap.get(year) ?? 0) + watts / 1000);
  }
  return yearMap;
}

function totalRepresentedKw(
  records: { year: number | string; total_watts?: number | string | null }[]
): number {
  let kw = 0;
  for (const record of records) {
    const year = parseInt(String(record.year), 10);
    if (!isSolarApprovalCohortYear(year)) continue;
    const watts = parseSourceNumber(record.total_watts);
    if (watts == null) continue;
    kw += watts / 1000;
  }
  return kw;
}

function latestPopulationYear(popRows: { year: number | string; population?: number | string | null }[]): {
  year: number;
  population: number;
} | null {
  let best: { year: number; population: number } | null = null;
  for (const row of popRows) {
    const year = parseInt(String(row.year), 10);
    if (!isSolarApprovalCohortYear(year)) continue;
    const pop = parseSourceNumber(row.population);
    if (pop == null || pop <= 0) continue;
    if (!best || year > best.year) best = { year, population: pop };
  }
  return best;
}

/** Current snapshot: total represented Project Sites DC kW (through cutoff) / latest compatible population. */
export async function computeSolarPerCapitaSnapshot(geofips: number): Promise<SolarPerCapitaSnapshot> {
  const empty: SolarPerCapitaSnapshot = {
    latestYear: null,
    localSolarKwPerResident: null,
    latestCapacityKw: null,
    sameYearPopulation: null,
  };
  const [solarRes, popRes] = await Promise.all([
    fetchAllPvSize({ select: 'year, sector, total_watts', geofips }),
    supabaseClient.from('population').select('year, population').eq('geofips', geofips).order('year', { ascending: true }),
  ]);
  if (solarRes.error || !solarRes.data?.length || popRes.error || !popRes.data?.length) {
    return empty;
  }

  const totalKw = totalRepresentedKw(solarRes.data);
  const pop = latestPopulationYear(popRes.data);

  if (pop == null || totalKw <= 0) return empty;

  return {
    latestYear: pop.year,
    localSolarKwPerResident: totalKw / pop.population,
    latestCapacityKw: totalKw,
    sameYearPopulation: pop.population,
  };
}

export async function computeSolarCumulativeSnapshot(geofips: number): Promise<SolarCumulativeSnapshot> {
  const empty: SolarCumulativeSnapshot = {
    latestYear: null,
    cumulativeSolarMw: null,
    latestAnnualAdditionMw: null,
    baselineYear: null,
    baselineCumulativeMw: null,
    cumulativeGrowthPct: null,
    post2015CohortSharePct: null,
  };
  const { data, error } = await fetchAllPvSize({ select: 'year, sector, total_watts', geofips });
  if (error || !data?.length) return empty;

  const annual = annualKwByYear(data);
  const years = Array.from(annual.keys()).sort((a, b) => a - b);
  if (!years.length) return empty;

  let cumulativeKw = 0;
  const series: SolarCumulativeSeriesPoint[] = [];
  for (const year of years) {
    const annualKw = annual.get(year) ?? 0;
    cumulativeKw += annualKw;
    series.push({ year, cumulativeKw, annualKw });
  }
  const latest = series[series.length - 1];
  const post2015Share = computePost2015CohortSharePct(series);

  return {
    latestYear: latest.year,
    cumulativeSolarMw: latest.cumulativeKw / 1000,
    latestAnnualAdditionMw: latest.annualKw / 1000,
    baselineYear: null,
    baselineCumulativeMw: null,
    cumulativeGrowthPct: null,
    post2015CohortSharePct: post2015Share,
  };
}

export { SOLAR_ANALYSIS_CUTOFF_YEAR };
